/* ============================================================
   MYO Acrylic Poster Builder — Core Logic
   CraftsBazaar | v1.1 (Phase 0 + 1, bug fixes)

   v1.1 fixes:
   - WHITE SCREEN BUG: Stage now operates at display resolution.
     Transformer handles render at sensible sizes instead of
     enormous scaled-up ones that covered the canvas.
   - Toast: stays visible until user closes with X button
   - Text editor: "Your text here" is now a placeholder hint
   - Font size slider: shows live numeric value
   - Builder fits viewport without page scroll
   ============================================================ */

(function () {
  'use strict';

  // ---- Configuration ----
  const CLOUDINARY_CLOUD_NAME = 'daz1q5ncg';
  const CLOUDINARY_UPLOAD_PRESET = 'myo_poster_unsigned';
  const AUTOSAVE_INTERVAL_MS = 30000;
  const LOCALSTORAGE_KEY = 'myo_poster_draft';
  const SAVES_KEY = 'myo_poster_saves';

  // Print-resolution pixel sizes at 300 DPI (used only for export)
  const PRINT_DIMENSIONS_PX = {
    'a4-portrait':   { w: 2480, h: 3508 },
    'a4-landscape':  { w: 3508, h: 2480 },
    'a3-portrait':   { w: 3508, h: 4961 },
    'a3-landscape':  { w: 4961, h: 3508 },
    'a2-portrait':   { w: 4961, h: 7016 },
    'a2-landscape':  { w: 7016, h: 4961 }
  };

  // Aspect ratios (paper-true, used for the on-screen canvas shape)
  const ASPECT_RATIOS = {
    'a4-portrait':   210 / 297,
    'a4-landscape':  297 / 210,
    'a3-portrait':   297 / 420,
    'a3-landscape':  420 / 297,
    'a2-portrait':   420 / 594,
    'a2-landscape':  594 / 420
  };

  // ---- State ----
  const state = {
    selectedVariant: null,
    canvasW: 0,
    canvasH: 0,
    printScale: 1,
    bgColor: '#ffffff',
    selectedNode: null,
    stage: null,
    bgLayer: null,
    designLayer: null,
    transformer: null,
    autosaveTimer: null,
    designId: null,
    history: [],
    historyMax: 30,
    isRestoring: false
  };

  const root = document.getElementById('myo-poster-root');
  if (!root) return;

  const $ = (sel, ctx = root) => ctx.querySelector(sel);
  const $$ = (sel, ctx = root) => Array.from(ctx.querySelectorAll(sel));

  const screens = {
    sizePicker: $('[data-screen="size-picker"]'),
    templatePicker: $('[data-screen="template-picker"]'),
    builder: $('[data-screen="builder"]')
  };

  const photoInput = $('#myo-photo-input');
  const stageContainer = $('#myo-stage-container');
  const selectionBar = $('[data-selection-bar]');
  const overlay = $('[data-overlay]');
  const toast = $('[data-toast]');

  // ============================================================
  // Screen navigation
  // ============================================================

  function showScreen(name) {
    Object.values(screens).forEach(s => s.hidden = true);
    if (screens[name]) screens[name].hidden = false;
    // Lock viewport scroll only while builder is open
    if (name === 'builder') {
      root.classList.add('myo-fullscreen');
      document.body.style.overflow = 'hidden';
    } else {
      root.classList.remove('myo-fullscreen');
      document.body.style.overflow = '';
    }
  }

  $$('[data-back-to]').forEach(btn => {
    btn.addEventListener('click', () => {
      const target = btn.dataset.backTo;
      const camelTarget = target.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
      showScreen(camelTarget);
    });
  });

  // ============================================================
  // Size picker
  // ============================================================

  $$('.myo-size-card').forEach(card => {
    card.addEventListener('click', () => {
      const variantTitle = card.dataset.variantTitle;
      const price = parseInt(card.dataset.price, 10);
      const key = variantTitle.toLowerCase().replace(/\s+/g, '-');
      const printDims = PRINT_DIMENSIONS_PX[key];
      const aspectRatio = ASPECT_RATIOS[key];
      if (!printDims || !aspectRatio) {
        showToast('Sorry, that size is not supported yet.');
        return;
      }

      state.selectedVariant = {
        id: card.dataset.variantId,
        title: variantTitle,
        price: price,
        key: key,
        printDimensions: printDims,
        aspectRatio: aspectRatio
      };

      $('[data-variant-display]').textContent = variantTitle;
      $('[data-price-display]').textContent = formatMoney(price);
      showScreen('templatePicker');
    });
  });

  // ============================================================
  // Template picker
  // ============================================================

  $$('.myo-template-card').forEach(card => {
    card.addEventListener('click', () => {
      initBuilder();
      showScreen('builder');
    });
  });

  // ============================================================
  // Builder init — uses DISPLAY resolution (fixes white screen)
  // ============================================================

  function initBuilder() {
    const v = state.selectedVariant;
    state.bgColor = '#ffffff';
    state.designId = generateDesignId();

    // Wait for fullscreen layout to settle before measuring
    requestAnimationFrame(() => {
      const dims = computeCanvasDimensions();
      state.canvasW = dims.w;
      state.canvasH = dims.h;
      state.printScale = v.printDimensions.w / state.canvasW;

      stageContainer.style.width = state.canvasW + 'px';
      stageContainer.style.height = state.canvasH + 'px';

      // Resize stage if already created
      if (state.stage) {
        state.stage.width(state.canvasW);
        state.stage.height(state.canvasH);
        const bgRect = state.bgLayer.findOne('.background');
        if (bgRect) {
          bgRect.width(state.canvasW);
          bgRect.height(state.canvasH);
        }
        state.stage.batchDraw();
      }
    });

    const dims = computeCanvasDimensions();
    state.canvasW = dims.w;
    state.canvasH = dims.h;
    state.printScale = v.printDimensions.w / state.canvasW;

    stageContainer.style.width = state.canvasW + 'px';
    stageContainer.style.height = state.canvasH + 'px';

    state.stage = new Konva.Stage({
      container: 'myo-stage-container',
      width: state.canvasW,
      height: state.canvasH
    });

    state.bgLayer = new Konva.Layer();
    const bgRect = new Konva.Rect({
      x: 0, y: 0,
      width: state.canvasW,
      height: state.canvasH,
      fill: state.bgColor,
      listening: true,
      name: 'background'
    });
    state.bgLayer.add(bgRect);
    state.stage.add(state.bgLayer);

    state.designLayer = new Konva.Layer();
    state.stage.add(state.designLayer);

    // Transformer with sensible display-resolution sizes (FIXED)
    state.transformer = new Konva.Transformer({
      rotateAnchorOffset: 24,
      enabledAnchors: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
      borderStroke: '#1d9e75',
      borderStrokeWidth: 2,
      anchorStroke: '#1d9e75',
      anchorFill: '#ffffff',
      anchorSize: 14,
      anchorStrokeWidth: 2,
      rotateAnchorCursor: 'grab'
    });
    state.designLayer.add(state.transformer);

    // Keep rotate input synced when user rotates via the transformer handle
    let transformStarted = false;
    state.transformer.on('transformstart', () => {
      if (!transformStarted) {
        pushHistory();
        transformStarted = true;
      }
    });
    state.transformer.on('transformend', () => {
      transformStarted = false;
    });
    state.transformer.on('transform', () => {
      if (state.selectedNode) {
        syncElementOptionsPanel(state.selectedNode);
      }
    });

    state.stage.on('click tap', (e) => {
      if (e.target === state.stage || e.target.name() === 'background') {
        deselect();
      }
    });

    state.stage.draw();
    resetZoom();
    clearHistory();
    loadDraftIfAny();
    startAutosave();
    updateUndoButton();
  }

  function computeCanvasDimensions() {
    const v = state.selectedVariant;
    const wrap = $('.myo-canvas-wrap');
    let wrapW = wrap.clientWidth;
    let wrapH = wrap.clientHeight;
    if (!wrapW || !wrapH) {
      wrapW = Math.min(window.innerWidth, 480) - 28;
      wrapH = window.innerHeight * 0.55;
    }
    const maxW = wrapW - 16;
    const maxH = wrapH - 16;
    let w, h;
    if (maxW / v.aspectRatio <= maxH) {
      w = maxW;
      h = maxW / v.aspectRatio;
    } else {
      h = maxH;
      w = maxH * v.aspectRatio;
    }
    return { w: Math.floor(w), h: Math.floor(h) };
  }

  // ============================================================
  // Selection management
  // ============================================================

  function selectNode(node) {
    state.selectedNode = node;
    state.transformer.nodes([node]);
    state.transformer.moveToTop();
    selectionBar.hidden = false;
    const cropBtn = selectionBar.querySelector('[data-crop-only]');
    if (cropBtn) cropBtn.style.display = node.getClassName() === 'Image' ? '' : 'none';

    // Show opacity + rotate strip for any selected element
    // Sync the element-options panel controls (panel may not be visible yet)
    syncElementOptionsPanel(node);

    if (node.getClassName() === 'Text') {
      populateTextEditor(node);
    }
  }

  function deselect() {
    if (deselect.skipHide) return;
    state.selectedNode = null;
    state.transformer.nodes([]);
    selectionBar.hidden = true;
    hidePanels();
  }

  function makeSelectable(node) {
    node.draggable(true);

    node.on('mousedown touchstart', (e) => {
      e.cancelBubble = true;
      selectNode(node);
    });

    node.on('click tap', (e) => {
      e.cancelBubble = true;
      selectNode(node);
    });

    node.on('dragstart', () => {
      pushHistory();
      selectNode(node);
    });

    // Double-tap / double-click — opens a panel for editing
    node.on('dblclick dbltap', (e) => {
      e.cancelBubble = true;
      e.evt && e.evt.preventDefault && e.evt.preventDefault();
      selectNode(node);

      if (node.getClassName() === 'Text') {
        openPanel('text-editor');
        // Sync all sliders + the rotate input + opacity to current node
        syncElementOptionsPanel(node);
        setTimeout(() => {
          const input = $('[data-text-input]');
          if (input) {
            input.focus();
            if (node.getAttr('isPlaceholder')) input.select();
          }
        }, 100);
      } else if (node.getClassName() === 'Image') {
        openPanel('element-options');
        syncElementOptionsPanel(node);
      }
    });
  }
  function syncElementOptionsPanel(node) {
    if (!node) return;
    const opacityVal = node.opacity();
    const pct = Math.round((opacityVal != null ? opacityVal : 1) * 100);

    // Sync ALL opacity sliders and displays (text editor + element-options panel)
    $$('[data-element-opacity]').forEach(el => { el.value = pct; });
    $$('[data-element-opacity-display]').forEach(el => { el.textContent = pct + '%'; });

    const rotation = Math.round(node.rotation() || 0);
    const sliderVal = ((rotation + 180) % 360 + 360) % 360 - 180;

    $$('[data-element-rotate-slider]').forEach(el => { el.value = sliderVal; });
    $$('[data-element-rotate]').forEach(el => { el.value = rotation; });
  }

  selectionBar.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-action]');
    if (!btn || !state.selectedNode) return;
    const action = btn.dataset.action;
    if (action === 'layer-forward') {
      state.selectedNode.moveUp();
      state.transformer.moveToTop();
    } else if (action === 'layer-back') {
      state.selectedNode.moveDown();
    } else if (action === 'duplicate') {
      pushHistory();
      const clone = state.selectedNode.clone({
        x: state.selectedNode.x() + 30,
        y: state.selectedNode.y() + 30
      });
      state.designLayer.add(clone);
      makeSelectable(clone);
      selectNode(clone);
    } else if (action === 'delete') {
      pushHistory();
      state.selectedNode.destroy();
      deselect();
    } else if (action.startsWith('align-')) {
      pushHistory();
      const node = state.selectedNode;
      const box = node.getClientRect({ relativeTo: state.designLayer });
      if (action === 'align-left') {
        node.x(node.x() - box.x);
      } else if (action === 'align-right') {
        node.x(node.x() + (state.canvasW - (box.x + box.width)));
      } else if (action === 'align-h-center') {
        node.x(node.x() + (state.canvasW / 2 - (box.x + box.width / 2)));
      } else if (action === 'align-top') {
        node.y(node.y() - box.y);
      } else if (action === 'align-bottom') {
        node.y(node.y() + (state.canvasH - (box.y + box.height)));
      } else if (action === 'align-v-center') {
        node.y(node.y() + (state.canvasH / 2 - (box.y + box.height / 2)));
      }
    } else if (action === 'crop') {
      startCropMode(state.selectedNode);
    }
  });

  // Element opacity — works for any selected node, uses event delegation
  // so it survives whether the slider was in DOM at init time
  // Opacity slider (in element-options panel)
  root.addEventListener('input', (e) => {
    if (!e.target.matches('[data-element-opacity]')) return;
    if (!state.selectedNode) return;
    e.stopPropagation();
    const pct = parseInt(e.target.value, 10);
    state.selectedNode.opacity(pct / 100);
    state.selectedNode.getLayer() && state.selectedNode.getLayer().batchDraw();
    // Sync both sliders + both displays
    $$('[data-element-opacity]').forEach(el => { if (el !== e.target) el.value = pct; });
    $$('[data-element-opacity-display]').forEach(el => { el.textContent = pct + '%'; });
  });

  // Rotation slider — drives the visual + syncs the number input
  root.addEventListener('input', (e) => {
    if (!e.target.matches('[data-element-rotate-slider]')) return;
    if (!state.selectedNode) return;
    e.stopPropagation();
    const deg = parseInt(e.target.value, 10);
    state.selectedNode.rotation(deg);
    state.selectedNode.getLayer() && state.selectedNode.getLayer().batchDraw();
    $$('[data-element-rotate-slider]').forEach(el => { if (el !== e.target) el.value = deg; });
    $$('[data-element-rotate]').forEach(el => { el.value = deg; });
  });

  // Rotation number input — precise typing
  root.addEventListener('input', (e) => {
    if (!e.target.matches('[data-element-rotate]')) return;
    if (!state.selectedNode) return;
    e.stopPropagation();
    let deg = parseFloat(e.target.value);
    if (isNaN(deg)) return;
    deg = Math.max(-360, Math.min(360, deg));
    state.selectedNode.rotation(deg);
    state.selectedNode.getLayer() && state.selectedNode.getLayer().batchDraw();
    const sliderVal = ((deg + 180) % 360 + 360) % 360 - 180;
    $$('[data-element-rotate-slider]').forEach(el => { el.value = sliderVal; });
    $$('[data-element-rotate]').forEach(el => { if (el !== e.target) el.value = deg; });
  });
  // Rotation input — also uses delegation
  root.addEventListener('input', (e) => {
    if (!e.target.matches('[data-element-rotate]')) return;
    if (!state.selectedNode) return;
    e.stopPropagation();
    let degrees = parseFloat(e.target.value);
    if (isNaN(degrees)) return;
    // Normalize to -360..360 just in case
    degrees = Math.max(-360, Math.min(360, degrees));
    state.selectedNode.rotation(degrees);
    state.selectedNode.getLayer() && state.selectedNode.getLayer().batchDraw();
  });

  // Sync rotate input when user rotates via the transformer handle
  if (state.transformer) {
    // Setup happens after transformer exists — we'll wire this in selectNode
  }
  // ============================================================
  // Photo upload
  // ============================================================

  $('.myo-tool[data-tool="photo"]').addEventListener('click', () => {
    photoInput.click();
  });

  photoInput.addEventListener('change', (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        // Quality check uses print dimensions
        const minRecommended = Math.min(state.selectedVariant.printDimensions.w, state.selectedVariant.printDimensions.h) / 3;
        if (Math.min(img.width, img.height) < minRecommended) {
          showToast('Heads up — this photo may look blurry when printed at full size.');
        }
        addPhotoToCanvas(img);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    photoInput.value = '';
  });

  function addPhotoToCanvas(img) {
    // Sized in DISPLAY pixels — start at 50% of smaller canvas dimension
    const targetSize = Math.min(state.canvasW, state.canvasH) * 0.5;
    const imgRatio = img.width / img.height;
    let w, h;
    if (imgRatio >= 1) {
      w = targetSize;
      h = targetSize / imgRatio;
    } else {
      h = targetSize;
      w = targetSize * imgRatio;
    }

    const photo = new Konva.Image({
      x: (state.canvasW - w) / 2,
      y: (state.canvasH - h) / 2,
      image: img,
      width: w,
      height: h,
      opacity: 1,
      name: 'photo'
    });
    photo.setAttr('sourceDataUrl', img.src);

    pushHistory();
    state.designLayer.add(photo);
    makeSelectable(photo);
    selectNode(photo);
    state.designLayer.batchDraw();
  }
// ============================================================
  // Crop mode (Batch B)
  // ============================================================
function startCropMode(imageNode) {
    if (!imageNode || imageNode.getClassName() !== 'Image') return;

    state.transformer.nodes([]);
    selectionBar.hidden = true;
    const opacityStrip = $('[data-opacity-strip]');
    if (opacityStrip) opacityStrip.hidden = true;
    deselect.skipHide = true;

    const box = imageNode.getClientRect({ relativeTo: state.stage });

    const cropLayer = new Konva.Layer({ name: 'cropLayer' });
    state.stage.add(cropLayer);

    // Dim — explicit listening:false
    const dimRect = new Konva.Rect({
      x: 0, y: 0,
      width: state.canvasW,
      height: state.canvasH,
      fill: 'rgba(0,0,0,0.55)',
      listening: false
    });
    cropLayer.add(dimRect);

    // Crop selection — proper opaque-enough fill to register hits
    const cropRect = new Konva.Rect({
      x: box.x,
      y: box.y,
      width: box.width,
      height: box.height,
      stroke: '#1d9e75',
      strokeWidth: 2,
      dash: [8, 4],
      fill: 'rgba(0, 200, 120, 0.08)',
      draggable: true,
      name: 'cropRect'
    });

    cropRect.dragBoundFunc(function (pos) {
      const w = this.width() * this.scaleX();
      const h = this.height() * this.scaleY();
      return {
        x: Math.max(box.x, Math.min(pos.x, box.x + box.width - w)),
        y: Math.max(box.y, Math.min(pos.y, box.y + box.height - h))
      };
    });
    cropLayer.add(cropRect);

    // Transformer — added LAST and configured AFTER add
    const cropTransformer = new Konva.Transformer({
      enabledAnchors: ['top-left', 'top-center', 'top-right', 'middle-right', 'bottom-right', 'bottom-center', 'bottom-left', 'middle-left'],
      rotateEnabled: false,
      borderEnabled: false,
      keepRatio: false,
      anchorStroke: '#1d9e75',
      anchorFill: '#ffffff',
      anchorSize: 18,
      anchorStrokeWidth: 2,
      anchorCornerRadius: 9,
      ignoreStroke: true,
      boundBoxFunc: (oldBox, newBox) => {
        const minSize = 30;
        if (newBox.width < minSize || newBox.height < minSize) return oldBox;
        if (newBox.x < box.x - 1) return oldBox;
        if (newBox.y < box.y - 1) return oldBox;
        if (newBox.x + newBox.width > box.x + box.width + 1) return oldBox;
        if (newBox.y + newBox.height > box.y + box.height + 1) return oldBox;
        return newBox;
      }
    });
    cropLayer.add(cropTransformer);
    cropTransformer.nodes([cropRect]);
    cropTransformer.forceUpdate();

    cropLayer.batchDraw();
    showCropBar(imageNode, cropRect, cropLayer);
  }

  function showCropBar(imageNode, cropRect, cropLayer) {
    let cropBar = document.querySelector('.myo-crop-bar');
    if (!cropBar) {
      cropBar = document.createElement('div');
      cropBar.className = 'myo-crop-bar';
      cropBar.innerHTML = `
        <button class="myo-crop-cancel">Cancel</button>
        <span class="myo-crop-hint">Drag corners to crop</span>
        <button class="myo-crop-apply">Apply</button>
      `;
      root.appendChild(cropBar);
    }
    cropBar.hidden = false;

    const cancelBtn = cropBar.querySelector('.myo-crop-cancel');
    const applyBtn = cropBar.querySelector('.myo-crop-apply');

    const cleanup = () => {
      cropLayer.destroy();
      cropBar.hidden = true;
      deselect.skipHide = false;
      selectNode(imageNode);
    };

    cancelBtn.onclick = cleanup;

    const performCrop = () => {
      try {
        // Push undo state BEFORE the crop happens
        if (typeof pushHistory === 'function') pushHistory();

        const imgBox = imageNode.getClientRect({ relativeTo: state.stage });
        const sourceImg = imageNode.image();

        if (!sourceImg || !imgBox.width || !imgBox.height) {
          console.warn('Crop aborted — invalid image bounds', imgBox);
          cleanup();
          return;
        }

        const curCropX = imageNode.cropX() || 0;
        const curCropY = imageNode.cropY() || 0;
        const curCropW = imageNode.cropWidth() || sourceImg.width;
        const curCropH = imageNode.cropHeight() || sourceImg.height;

        const ratioX = curCropW / imgBox.width;
        const ratioY = curCropH / imgBox.height;

        const cropW = cropRect.width() * cropRect.scaleX();
        const cropH = cropRect.height() * cropRect.scaleY();

        const newCropX = curCropX + (cropRect.x() - imgBox.x) * ratioX;
        const newCropY = curCropY + (cropRect.y() - imgBox.y) * ratioY;
        const newCropW = cropW * ratioX;
        const newCropH = cropH * ratioY;

        // Sanity-check the values before applying
        if (!isFinite(newCropX) || !isFinite(newCropY) || !isFinite(newCropW) || !isFinite(newCropH) || newCropW < 1 || newCropH < 1) {
          console.warn('Crop aborted — invalid crop values', { newCropX, newCropY, newCropW, newCropH });
          showToast('Could not apply crop. Try again.');
          cleanup();
          return;
        }

        imageNode.crop({
          x: Math.max(0, newCropX),
          y: Math.max(0, newCropY),
          width: newCropW,
          height: newCropH
        });
        imageNode.width(cropW);
        imageNode.height(cropH);
        imageNode.x(cropRect.x());
        imageNode.y(cropRect.y());
        imageNode.scaleX(1);
        imageNode.scaleY(1);
        imageNode.rotation(0);

        imageNode.clearCache();
        imageNode.draggable(true);

        state.designLayer.batchDraw();
      } catch (err) {
        console.error('Crop apply failed:', err);
        showToast('Could not apply crop. Try again.');
      }
      cleanup();
    };

    // Bind on multiple event types — touch devices sometimes don't fire onclick reliably
    applyBtn.onclick = performCrop;
    applyBtn.addEventListener('touchend', (e) => {
      e.preventDefault();
      performCrop();
    });
  }
  
  // ============================================================
  // Text tool (placeholder behavior FIXED)
  // ============================================================

  $('.myo-tool[data-tool="text"]').addEventListener('click', () => {
    addTextToCanvas();
  });

  function addTextToCanvas() {
    const fontSize = Math.round(state.canvasW * 0.06);

    // Create text WITHOUT align: 'center' — it interferes with hit detection
    // when there's no fixed width. We position via x/y + offset instead.
    const text = new Konva.Text({
      x: state.canvasW / 2,
      y: state.canvasH / 2,
      text: 'Tap to edit',
      fontSize: fontSize,
      fontFamily: 'Poppins',
      fill: '#000000',
      name: 'text'
    });

    // Apply max width only if text would overflow
    const maxWidth = state.canvasW * 0.85;
    if (text.width() > maxWidth) {
      text.width(maxWidth);
      text.align('center'); // safe to set when width is fixed
    }

    text.setAttr('isPlaceholder', true);
    text.offsetX(text.width() / 2);
    text.offsetY(text.height() / 2);

    pushHistory();
    state.designLayer.add(text);
    makeSelectable(text);
    selectNode(text);
    state.designLayer.batchDraw();

    openPanel('text-editor');
    setTimeout(() => {
      const input = $('[data-text-input]');
      if (input) {
        input.focus();
        input.select();
      }
    }, 100);
  }

  function populateTextEditor(node) {
    const input = $('[data-text-input]');
    // If it's still the placeholder, show empty input
    if (node.getAttr('isPlaceholder')) {
      input.value = '';
      input.placeholder = 'Type your text…';
    } else {
      input.value = node.text();
      input.placeholder = 'Type your text…';
    }
    $('[data-font-select]').value = node.fontFamily();
    const sliderVal = Math.round(node.fontSize() / state.canvasW * 1000);
    $('[data-text-size]').value = sliderVal;
    $('[data-text-size-display]').textContent = Math.round(node.fontSize()) + 'px';
  }

  $('[data-text-input]').addEventListener('input', (e) => {
    if (state.selectedNode && state.selectedNode.getClassName() === 'Text') {
      state.selectedNode.setAttr('isPlaceholder', false);
      const newText = e.target.value || ' ';
      const node = state.selectedNode;

      node.width('auto');
      node.align('left'); // reset so width() returns natural width
      node.text(newText);

      const maxWidth = state.canvasW * 0.85;
      if (node.width() > maxWidth) {
        node.width(maxWidth);
        node.align('center');
      }

      node.offsetX(node.width() / 2);
      node.offsetY(node.height() / 2);
      state.designLayer.batchDraw();
    }
  });

  $('[data-font-select]').addEventListener('change', (e) => {
    if (state.selectedNode && state.selectedNode.getClassName() === 'Text') {
      const node = state.selectedNode;
      node.fontFamily(e.target.value);
      const currentText = node.text();
      node.width('auto');
      node.align('left');
      node.text(currentText);
      const maxWidth = state.canvasW * 0.85;
      if (node.width() > maxWidth) {
        node.width(maxWidth);
        node.align('center');
      }
      node.offsetX(node.width() / 2);
      node.offsetY(node.height() / 2);
      state.designLayer.batchDraw();
    }
  });

  // Slider shows actual pixel size now, not abstract slider value
  $('[data-text-size]').addEventListener('input', (e) => {
    const sliderVal = parseInt(e.target.value, 10);
    if (state.selectedNode && state.selectedNode.getClassName() === 'Text') {
      const node = state.selectedNode;
      const newSize = sliderVal / 1000 * state.canvasW;
      node.fontSize(newSize);
      const currentText = node.text();
      node.width('auto');
      node.align('left');
      node.text(currentText);
      const maxWidth = state.canvasW * 0.85;
      if (node.width() > maxWidth) {
        node.width(maxWidth);
        node.align('center');
      }
      node.offsetX(node.width() / 2);
      node.offsetY(node.height() / 2);
      state.designLayer.batchDraw();
      $('[data-text-size-display]').textContent = Math.round(newSize) + 'px';
    } else {
      $('[data-text-size-display]').textContent = sliderVal;
    }
  });

  $$('[data-color-swatches] .myo-swatch').forEach(sw => {
    sw.addEventListener('click', () => {
      if (state.selectedNode && state.selectedNode.getClassName() === 'Text') {
        state.selectedNode.fill(sw.dataset.color);
        $$('[data-color-swatches] .myo-swatch').forEach(s => s.classList.remove('active'));
        sw.classList.add('active');
      }
    });
  });

  $$('.myo-align-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      if (state.selectedNode && state.selectedNode.getClassName() === 'Text') {
        state.selectedNode.align(btn.dataset.align);
        $$('.myo-align-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
      }
    });
  });

  $$('.myo-style-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      if (!state.selectedNode || state.selectedNode.getClassName() !== 'Text') return;
      btn.classList.toggle('active');
      const styleSet = $$('.myo-style-btn.active').map(b => b.dataset.style);
      let style = '';
      if (styleSet.includes('bold')) style += 'bold ';
      if (styleSet.includes('italic')) style += 'italic';
      state.selectedNode.fontStyle(style.trim() || 'normal');
    });
  });

  // ============================================================
  // Background
  // ============================================================

  $('.myo-tool[data-tool="background"]').addEventListener('click', () => {
    openPanel('background');
  });

  function setBackgroundColor(color) {
    if (!/^#[0-9a-fA-F]{6}$/.test(color)) return false;
    if (state.bgColor !== color) pushHistory();
    state.bgColor = color;
    const bgRect = state.bgLayer.findOne('.background');
    if (bgRect) bgRect.fill(color);
    const cp = $('[data-bg-colorpick]');
    const hex = $('[data-bg-hex]');
    if (cp) cp.value = color;
    if (hex) hex.value = color.toUpperCase();
    return true;
  }

  // Swatch clicks — apply and close panel
  $$('.myo-bg-swatch').forEach(sw => {
    sw.addEventListener('click', () => {
      setBackgroundColor(sw.dataset.bg);
      hidePanels();
    });
  });

  // Native color picker — live update as user scrubs
  const bgColorPick = $('[data-bg-colorpick]');
  if (bgColorPick) {
    bgColorPick.addEventListener('input', (e) => {
      setBackgroundColor(e.target.value);
    });
  }

  // HEX input — validate on enter or blur
  const bgHexInput = $('[data-bg-hex]');
  if (bgHexInput) {
    const applyHex = () => {
      let val = bgHexInput.value.trim();
      if (!val.startsWith('#')) val = '#' + val;
      if (!setBackgroundColor(val)) {
        showToast('Invalid hex code. Use format like #FF0066.');
        bgHexInput.value = state.bgColor.toUpperCase();
      }
    };
    bgHexInput.addEventListener('blur', applyHex);
    bgHexInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        applyHex();
        bgHexInput.blur();
      }
    });
  }

  // "Set" button — apply hex and close panel
  const bgApplyBtn = $('[data-bg-apply]');
  if (bgApplyBtn) {
    bgApplyBtn.addEventListener('click', () => {
      let val = bgHexInput.value.trim();
      if (!val.startsWith('#')) val = '#' + val;
      if (setBackgroundColor(val)) {
        hidePanels();
      } else {
        showToast('Invalid hex code. Use format like #FF0066.');
      }
    });
  }

  // ============================================================
  // Canvas zoom (user-controlled view size)
  // ============================================================

  let currentZoom = 100;

  function applyZoom(zoomPct) {
    currentZoom = Math.max(50, Math.min(150, zoomPct));
    if (stageContainer) {
      stageContainer.style.transform = `scale(${currentZoom / 100})`;
    }
    const valueDisplay = $('[data-zoom-value]');
    const slider = $('[data-zoom-slider]');
    if (valueDisplay) valueDisplay.textContent = currentZoom + '%';
    if (slider && parseInt(slider.value, 10) !== currentZoom) {
      slider.value = currentZoom;
    }
  }

  const zoomSlider = $('[data-zoom-slider]');
  if (zoomSlider) {
    zoomSlider.addEventListener('input', (e) => {
      applyZoom(parseInt(e.target.value, 10));
    });
  }

  $$('[data-zoom-action]').forEach(btn => {
    btn.addEventListener('click', () => {
      const action = btn.dataset.zoomAction;
      const step = 10;
      applyZoom(action === 'in' ? currentZoom + step : currentZoom - step);
    });
  });

  // Reset zoom whenever a new design starts
  function resetZoom() {
    applyZoom(100);
  }

  // ============================================================
  // Panels
  // ============================================================

  function openPanel(name) {
    hidePanels();
    const panel = $(`[data-panel="${name}"]`);
    if (panel) {
      panel.hidden = false;
      overlay.hidden = false;
    }
  }

  function hidePanels() {
    $$('.myo-panel').forEach(p => p.hidden = true);
    overlay.hidden = true;
  }

  $$('.myo-panel-close').forEach(btn => {
    btn.addEventListener('click', hidePanels);
  });

  overlay.addEventListener('click', hidePanels);

  // ============================================================
  // Autosave
  // ============================================================

  function startAutosave() {
    if (state.autosaveTimer) clearInterval(state.autosaveTimer);
    state.autosaveTimer = setInterval(saveDraft, AUTOSAVE_INTERVAL_MS);
  }

  function saveDraft() {
    if (!state.stage) return;
    try {
      const draft = serializeDesign();
      localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(draft));
    } catch (err) {
      console.warn('Autosave failed:', err);
    }
  }

  function loadDraftIfAny() {
    try {
      const raw = localStorage.getItem(LOCALSTORAGE_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (!draft.variantId || draft.variantId !== state.selectedVariant.id) return;
      if (!confirm('Restore your last unsaved design?')) {
        localStorage.removeItem(LOCALSTORAGE_KEY);
        return;
      }
      restoreDraft(draft);
    } catch (err) {
      console.warn('Failed to load draft:', err);
    }
  }
// ============================================================
  // Undo history
  // ============================================================

  function pushHistory() {
    // Skip if we're currently restoring from history
    if (state.isRestoring) return;
    if (!state.designLayer) return;
    try {
      const snapshot = serializeDesign();
      state.history.push(snapshot);
      // Cap history size
      if (state.history.length > state.historyMax) {
        state.history.shift();
      }
      updateUndoButton();
    } catch (err) {
      console.warn('History push failed:', err);
    }
  }

  function undo() {
    if (state.history.length === 0) {
      showToast('Nothing to undo.');
      return;
    }
    const previous = state.history.pop();
    state.isRestoring = true;
    try {
      // Deselect FIRST so transformer doesn't hold references to dying nodes
      deselect();
      state.transformer.nodes([]);

      // Hard-clear the design layer — remove EVERY child including transformer,
      // then re-add a fresh transformer. This guarantees no stale references.
      state.designLayer.destroyChildren();

      // Recreate the transformer (it was destroyed above)
      state.transformer = new Konva.Transformer({
        rotateAnchorOffset: 24,
        enabledAnchors: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
        borderStroke: '#1d9e75',
        borderStrokeWidth: 2,
        anchorStroke: '#1d9e75',
        anchorFill: '#ffffff',
        anchorSize: 14,
        anchorStrokeWidth: 2,
        rotateAnchorCursor: 'grab'
      });
      state.designLayer.add(state.transformer);

      // Re-attach transform listeners (they were on the old transformer)
      let transformStarted = false;
      state.transformer.on('transformstart', () => {
        if (!transformStarted) {
          pushHistory();
          transformStarted = true;
        }
      });
      state.transformer.on('transformend', () => {
        transformStarted = false;
      });
      state.transformer.on('transform', () => {
        if (state.selectedNode) {
          syncElementOptionsPanel(state.selectedNode);
        }
      });

      // Restore background color
      state.bgColor = previous.bgColor;
      const bgRect = state.bgLayer.findOne('.background');
      if (bgRect) bgRect.fill(previous.bgColor);

      // Restore elements from the snapshot
      restoreDraft(previous);
      state.designLayer.batchDraw();
    } catch (err) {
      console.error('Undo failed:', err);
      showToast('Could not undo.');
    }
    state.isRestoring = false;
    updateUndoButton();
  }

  function updateUndoButton() {
    const btn = $('[data-action="undo"]');
    if (!btn) return;
    btn.disabled = state.history.length === 0;
  }

  function clearHistory() {
    state.history = [];
    updateUndoButton();
  }

  // Wire up the undo button
  const undoBtn = $('[data-action="undo"]');
  if (undoBtn) {
    undoBtn.addEventListener('click', undo);
  }

  // Keyboard shortcut (desktop)
  document.addEventListener('keydown', (e) => {
    // Only trigger if builder is the active screen
    if (screens.builder.hidden) return;
    // Ignore when typing in inputs
    const tag = (e.target.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
      e.preventDefault();
      undo();
    }
  });
  function serializeDesign() {
    const nodes = state.designLayer.getChildren().filter(n => n !== state.transformer);
    return {
      designId: state.designId,
      variantId: state.selectedVariant.id,
      variantTitle: state.selectedVariant.title,
      canvasW: state.canvasW,
      canvasH: state.canvasH,
      printScale: state.printScale,
      bgColor: state.bgColor,
      timestamp: Date.now(),
      elements: nodes.map(n => {
        const base = {
          type: n.getClassName(),
          x: n.x(), y: n.y(),
          rotation: n.rotation(),
          scaleX: n.scaleX(), scaleY: n.scaleY(),
          offsetX: n.offsetX(), offsetY: n.offsetY(),
          width: n.width(), height: n.height()
        };
        if (n.getClassName() === 'Text') {
          return {
            ...base,
            text: n.text(),
            fontSize: n.fontSize(),
            fontFamily: n.fontFamily(),
            fontStyle: n.fontStyle(),
            fill: n.fill(),
            align: n.align(),
            opacity: n.opacity(),
            isPlaceholder: n.getAttr('isPlaceholder')
          };
        }
        if (n.getClassName() === 'Image') {
          const sourceImg = n.image();
          return {
            ...base,
            sourceDataUrl: n.getAttr('sourceDataUrl'),
            // Preserve crop info — critical for undo of cropped images
            cropX: n.cropX() || 0,
            cropY: n.cropY() || 0,
            cropWidth: n.cropWidth() || (sourceImg ? sourceImg.width : null),
            cropHeight: n.cropHeight() || (sourceImg ? sourceImg.height : null),
            opacity: n.opacity()
          };
        }
        return base;
      })
    };
  }

  function restoreDraft(draft) {
    state.designId = draft.designId;
    state.bgColor = draft.bgColor;
    state.bgLayer.findOne('.background').fill(draft.bgColor);

    draft.elements.forEach(el => {
      if (el.type === 'Text') {
        const t = new Konva.Text({
          x: el.x, y: el.y,
          rotation: el.rotation,
          scaleX: el.scaleX, scaleY: el.scaleY,
          offsetX: el.offsetX, offsetY: el.offsetY,
          width: el.width || undefined,
          text: el.text,
          fontSize: el.fontSize,
          fontFamily: el.fontFamily,
          fontStyle: el.fontStyle,
          fill: el.fill,
          align: el.align,
          opacity: (el.opacity != null) ? el.opacity : 1,
          name: 'text'
        });
        if (el.isPlaceholder) t.setAttr('isPlaceholder', true);
        state.designLayer.add(t);
        makeSelectable(t);
      } else if (el.type === 'Image' && el.sourceDataUrl) {
        const img = new Image();
        img.onload = () => {
          const config = {
            x: el.x, y: el.y,
            image: img,
            width: el.width,
            height: el.height,
            rotation: el.rotation,
            scaleX: el.scaleX, scaleY: el.scaleY,
            opacity: (el.opacity != null) ? el.opacity : 1,
            name: 'photo'
          };
          // Apply crop if present — critical for cropped images
          if (el.cropX != null && el.cropWidth != null) {
            config.crop = {
              x: el.cropX,
              y: el.cropY,
              width: el.cropWidth,
              height: el.cropHeight
            };
          }
          const k = new Konva.Image(config);
          k.setAttr('sourceDataUrl', el.sourceDataUrl);
          state.designLayer.add(k);
          makeSelectable(k);
          state.designLayer.batchDraw();
        };
        img.src = el.sourceDataUrl;
      }
    });
  }
// ============================================================
  // Save library (Batch C)
  // ============================================================

  function getSaves() {
    try {
      return JSON.parse(localStorage.getItem(SAVES_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  function writeSaves(saves) {
    try {
      localStorage.setItem(SAVES_KEY, JSON.stringify(saves));
    } catch (e) {
      showToast('Out of storage space — try deleting old saves.', true);
    }
  }

  function saveCurrentDesign(name) {
    const elements = state.designLayer.getChildren().filter(n => n !== state.transformer);
    if (!elements.length) {
      showToast('Add at least one element before saving.');
      return null;
    }
    // Capture preview thumbnail (small)
    state.transformer.nodes([]);
    state.stage.draw();
    const thumbnail = state.stage.toDataURL({
      pixelRatio: 200 / state.canvasW,
      mimeType: 'image/jpeg',
      quality: 0.7
    });
    if (state.selectedNode) state.transformer.nodes([state.selectedNode]);

    const design = serializeDesign();
    const save = {
      id: design.designId,
      name: name || `Design ${new Date().toLocaleDateString('en-IN')}`,
      thumbnail,
      design,
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    const saves = getSaves();
    // If we're editing a save with the same designId, update it
    const existingIdx = saves.findIndex(s => s.id === save.id);
    if (existingIdx >= 0) {
      save.createdAt = saves[existingIdx].createdAt;
      save.name = name || saves[existingIdx].name;
      saves[existingIdx] = save;
    } else {
      saves.unshift(save);
    }
    writeSaves(saves);
    return save;
  }

  function loadSave(saveId) {
    const saves = getSaves();
    const save = saves.find(s => s.id === saveId);
    if (!save) return;

    // Ensure variant matches; if not, switch
    if (save.design.variantId !== state.selectedVariant?.id) {
      // Switch variant
      const card = document.querySelector(`.myo-size-card[data-variant-id="${save.design.variantId}"]`);
      if (card) {
        card.click(); // sets selectedVariant
        // Go directly to builder
        initBuilder();
        showScreen('builder');
      }
    }

    // Clear current canvas
    state.designLayer.getChildren().forEach(n => {
      if (n !== state.transformer) n.destroy();
    });
    deselect();

    // Restore
    state.designId = save.design.designId;
    state.bgColor = save.design.bgColor;
    state.bgLayer.findOne('.background').fill(save.design.bgColor);
    restoreDraft(save.design);
    hidePanels();
    showToast(`Loaded "${save.name}"`);
  }

  function deleteSave(saveId) {
    if (!confirm('Delete this saved design? This cannot be undone.')) return;
    const saves = getSaves().filter(s => s.id !== saveId);
    writeSaves(saves);
    renderSavesList();
  }

  function renameSave(saveId) {
    const saves = getSaves();
    const save = saves.find(s => s.id === saveId);
    if (!save) return;
    const newName = prompt('Rename design:', save.name);
    if (newName && newName.trim()) {
      save.name = newName.trim();
      save.updatedAt = Date.now();
      writeSaves(saves);
      renderSavesList();
    }
  }

  function renderSavesList() {
    const list = $('[data-saves-list]');
    const saves = getSaves();
    if (!saves.length) {
      list.innerHTML = '<div class="myo-saves-empty">No saved designs yet.</div>';
      return;
    }
    list.innerHTML = saves.map(s => `
      <div class="myo-save-item" data-save-id="${s.id}">
        <img class="myo-save-thumb" src="${s.thumbnail}" alt="${s.name}">
        <div class="myo-save-info">
          <div class="myo-save-name">${s.name}</div>
          <div class="myo-save-meta">${s.design.variantTitle} · ${new Date(s.updatedAt).toLocaleDateString('en-IN')}</div>
        </div>
        <div class="myo-save-actions">
          <button class="myo-save-btn" data-save-action="load" data-save-id="${s.id}">Edit</button>
          <button class="myo-save-btn" data-save-action="duplicate" data-save-id="${s.id}">Duplicate</button>
          <button class="myo-save-btn" data-save-action="rename" data-save-id="${s.id}">Rename</button>
          <button class="myo-save-btn myo-save-btn-danger" data-save-action="delete" data-save-id="${s.id}">Delete</button>
        </div>
      </div>
    `).join('');
  }

  // Open saves panel
  $('[data-action="open-saves"]').addEventListener('click', () => {
    renderSavesList();
    openPanel('saves');
  });

  // Save current button (inside saves panel)
  $('[data-action="save-current"]').addEventListener('click', () => {
    const name = prompt('Name this design:', `Design ${new Date().toLocaleDateString('en-IN')}`);
    if (name === null) return; // cancelled
    const result = saveCurrentDesign(name.trim());
    if (result) {
      showToast(`Saved as "${result.name}"`);
      renderSavesList();
    }
  });

  // Save button in CTA bar
  $('[data-action="save"]').addEventListener('click', () => {
    const name = prompt('Name this design:', `Design ${new Date().toLocaleDateString('en-IN')}`);
    if (name === null) return;
    const result = saveCurrentDesign(name.trim());
    if (result) showToast(`Saved as "${result.name}"`);
  });

  // Save item actions (delegated)
  $('[data-saves-list]').addEventListener('click', (e) => {
    const btn = e.target.closest('[data-save-action]');
    if (!btn) return;
    const action = btn.dataset.saveAction;
    const saveId = btn.dataset.saveId;
    if (action === 'load') loadSave(saveId);
    else if (action === 'duplicate') {
      const saves = getSaves();
      const original = saves.find(s => s.id === saveId);
      if (!original) return;
      const copy = JSON.parse(JSON.stringify(original));
      copy.id = generateDesignId();
      copy.design.designId = copy.id;
      copy.name = original.name + ' (copy)';
      copy.createdAt = Date.now();
      copy.updatedAt = Date.now();
      saves.unshift(copy);
      writeSaves(saves);
      renderSavesList();
    }
    else if (action === 'rename') renameSave(saveId);
    else if (action === 'delete') deleteSave(saveId);
  });
  // ============================================================
  // Add to cart (exports at print resolution)
  // ============================================================
// Download — exports current canvas as PNG at print resolution
  $('[data-action="download"]').addEventListener('click', () => {
    if (!state.designLayer.getChildren().filter(n => n !== state.transformer).length) {
      showToast('Add at least one element first.');
      return;
    }
    state.transformer.nodes([]);
    state.stage.draw();
    const url = state.stage.toDataURL({
      pixelRatio: state.printScale,
      mimeType: 'image/png'
    });
    if (state.selectedNode) state.transformer.nodes([state.selectedNode]);
    const link = document.createElement('a');
    link.download = `myo-poster-${state.designId}.png`;
    link.href = url;
    link.click();
  });

  $('[data-action="add-to-cart"]').addEventListener('click', async () => {
    const elements = state.designLayer.getChildren().filter(n => n !== state.transformer);
    if (!elements.length) {
      showToast('Add at least one photo or text first.', true);
      return;
    }

    const btn = $('[data-action="add-to-cart"]');
    const originalLabel = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = 'Saving design…';

    try {
      state.transformer.nodes([]);
      state.stage.draw();

      // Preview at ~600px wide
      const previewDataUrl = state.stage.toDataURL({
        pixelRatio: 600 / state.canvasW,
        mimeType: 'image/png'
      });

      // Print export — scale up to print resolution
      const printDataUrl = state.stage.toDataURL({
        pixelRatio: state.printScale,
        mimeType: 'image/png'
      });

      if (state.selectedNode) state.transformer.nodes([state.selectedNode]);

      const previewUrl = await uploadToCloudinary(previewDataUrl, `${state.designId}_preview`);
      const printUrl = await uploadToCloudinary(printDataUrl, `${state.designId}_print`);

      await addToShopifyCart({
        id: state.selectedVariant.id,
        quantity: 1,
        properties: {
          'Design ID': state.designId,
          'Preview': previewUrl,
          'Print File': printUrl,
          '_design_json': JSON.stringify(serializeDesign())
        }
      });

      localStorage.removeItem(LOCALSTORAGE_KEY);
      window.location.href = '/cart';
    } catch (err) {
      console.error('Add to cart failed:', err);
      showToast('Sorry, something went wrong. Please try again.', true);
      btn.disabled = false;
      btn.innerHTML = originalLabel;
    }
  });

  async function uploadToCloudinary(dataUrl, publicId) {
    if (CLOUDINARY_CLOUD_NAME === 'YOUR_CLOUD_NAME') {
      console.warn('Cloudinary not configured');
      return dataUrl.substring(0, 100) + '...[unsaved]';
    }
    const formData = new FormData();
    formData.append('file', dataUrl);
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
    formData.append('public_id', publicId);
    const res = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
      { method: 'POST', body: formData }
    );
    if (!res.ok) throw new Error('Cloudinary upload failed');
    const data = await res.json();
    return data.secure_url;
  }

  async function addToShopifyCart(item) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [item] })
    });
    if (!res.ok) throw new Error('Cart add failed');
    return res.json();
  }

  // ============================================================
  // Helpers — toast now persistent with close button
  // ============================================================

  function generateDesignId() {
    const ts = Date.now().toString(36);
    const rand = Math.random().toString(36).substring(2, 8);
    return `myo_${ts}_${rand}`;
  }

  function formatMoney(paise) {
    const rupees = paise / 100;
    return '₹' + rupees.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }

  function showToast(msg, persistent = false) {
    // Build toast content with optional close button
    toast.innerHTML = `
      <span class="myo-toast-msg">${msg}</span>
      <button class="myo-toast-close" aria-label="Dismiss">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    `;
    toast.hidden = false;
    const closeBtn = toast.querySelector('.myo-toast-close');
    closeBtn.addEventListener('click', () => { toast.hidden = true; });
    clearTimeout(showToast._t);
    if (!persistent) {
      showToast._t = setTimeout(() => { toast.hidden = true; }, 8000);
    }
  }

  // Auto-show toast as persistent for important warnings
  // The low-res warning is important so we make it stay
  const _origShowToast = showToast;
  window.__myoToast = showToast; // exposed for debugging

  // ============================================================
  // Resize handling
  // ============================================================

  window.addEventListener('resize', () => {
    if (!state.stage) return;
    const newDims = computeCanvasDimensions();
    const wScale = newDims.w / state.canvasW;
    const hScale = newDims.h / state.canvasH;

    state.canvasW = newDims.w;
    state.canvasH = newDims.h;
    state.printScale = state.selectedVariant.printDimensions.w / state.canvasW;

    state.stage.width(state.canvasW);
    state.stage.height(state.canvasH);
    stageContainer.style.width = state.canvasW + 'px';
    stageContainer.style.height = state.canvasH + 'px';

    // Resize background
    const bgRect = state.bgLayer.findOne('.background');
    if (bgRect) {
      bgRect.width(state.canvasW);
      bgRect.height(state.canvasH);
    }

    // Scale all design elements proportionally
    state.designLayer.getChildren().forEach(n => {
      if (n === state.transformer) return;
      n.x(n.x() * wScale);
      n.y(n.y() * hScale);
      n.scaleX(n.scaleX() * wScale);
      n.scaleY(n.scaleY() * hScale);
    });

    state.stage.batchDraw();
  });

})();
